import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserCog, UserCheck, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import {
  getAllMembers,
  getPendingMembers,
  approveMember,
  deleteMember,
  toggleDiscordBot,
  AdminMemberInfo,
} from '@/api/adminApi';

/**
 * 관리자 페이지
 * - 회원 목록 조회 및 강제 탈퇴
 * - 가입 대기 회원 승인/거절
 * - Discord Bot on/off
 */
export default function AdminPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [allMembers, setAllMembers] = useState<AdminMemberInfo[]>([]);
  const [pendingMembers, setPendingMembers] = useState<AdminMemberInfo[]>([]);
  const [discordBotStatus, setDiscordBotStatus] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [botLoading, setBotLoading] = useState(false);

  useEffect(() => {
    loadAllMembers();
    loadPendingMembers();
  }, []);

  /**
   * 전체 회원 목록 로드
   */
  const loadAllMembers = async () => {
    try {
      setLoading(true);
      const data = await getAllMembers();
      // 가입일 기준 내림차순 정렬 (최신 가입자가 위로)
      const sortedData = data.sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      setAllMembers(sortedData);
    } catch (error) {
      console.error('회원 목록 로드 실패:', error);
      toast({
        title: '오류',
        description: '회원 목록을 불러오는데 실패했습니다.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * 가입 대기 회원 목록 로드
   */
  const loadPendingMembers = async () => {
    try {
      const data = await getPendingMembers();
      // 신청일 기준 내림차순 정렬 (최신 신청자가 위로)
      const sortedData = data.sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      setPendingMembers(sortedData);
    } catch (error) {
      console.error('가입 대기 목록 로드 실패:', error);
      toast({
        title: '오류',
        description: '가입 대기 목록을 불러오는데 실패했습니다.',
        variant: 'destructive',
      });
    }
  };

  /**
   * 회원 승인
   */
  const handleApproveMember = async (memberId: string, nickname: string) => {
    try {
      await approveMember(memberId);
      toast({
        title: '승인 완료',
        description: `${nickname} 회원이 승인되었습니다.`,
      });
      loadPendingMembers();
      loadAllMembers();
    } catch (error) {
      console.error('회원 승인 실패:', error);
      toast({
        title: '오류',
        description: '회원 승인에 실패했습니다.',
        variant: 'destructive',
      });
    }
  };

  /**
   * 회원 삭제 (승인 거절 또는 강제 탈퇴)
   */
  const handleDeleteMember = async (memberId: string, nickname: string, isPending: boolean) => {
    const confirmMessage = isPending
      ? `${nickname} 회원의 가입을 거절하시겠습니까?`
      : `${nickname} 회원을 강제 탈퇴시키시겠습니까?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      await deleteMember(memberId);
      toast({
        title: isPending ? '가입 거절' : '강제 탈퇴',
        description: `${nickname} 회원이 삭제되었습니다.`,
      });
      if (isPending) {
        loadPendingMembers();
      } else {
        loadAllMembers();
      }
    } catch (error) {
      console.error('회원 삭제 실패:', error);
      toast({
        title: '오류',
        description: '회원 삭제에 실패했습니다.',
        variant: 'destructive',
      });
    }
  };

  /**
   * Discord Bot on/off 토글
   */
  const handleToggleDiscordBot = async () => {
    try {
      setBotLoading(true);
      const status = await toggleDiscordBot();
      setDiscordBotStatus(status);
      toast({
        title: 'Discord Bot',
        description: status === '봇on' ? 'Discord Bot이 켜졌습니다.' : 'Discord Bot이 꺼졌습니다.',
      });
    } catch (error) {
      console.error('Discord Bot 토글 실패:', error);
      toast({
        title: '오류',
        description: 'Discord Bot 상태 변경에 실패했습니다.',
        variant: 'destructive',
      });
    } finally {
      setBotLoading(false);
    }
  };

  /**
   * 날짜 포맷팅
   */
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* 헤더 */}
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 h-14 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/schedule')} className="h-9 w-9">
            <ArrowLeft className="h-5 w-5" />
            <span className="sr-only">뒤로 가기</span>
          </Button>
          <div className="flex items-center gap-2">
            <UserCog className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-bold">관리자 페이지</h1>
          </div>
        </div>
      </header>

      {/* 본문 */}
      <main className="container mx-auto px-4 py-6 max-w-6xl">
        <Tabs defaultValue="members" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="members" className="gap-2">
              <UserCog className="h-4 w-4" />
              회원 목록
            </TabsTrigger>
            <TabsTrigger value="pending" className="gap-2">
              <UserCheck className="h-4 w-4" />
              가입 대기
              {pendingMembers.length > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs">
                  {pendingMembers.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="discord" className="gap-2">
              <Bot className="h-4 w-4" />
              Discord Bot
            </TabsTrigger>
          </TabsList>

          {/* 회원 목록 */}
          <TabsContent value="members">
            <Card>
              <CardHeader>
                <CardTitle>전체 회원 목록</CardTitle>
                <CardDescription>총 {allMembers.length}명의 회원이 있습니다.</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center text-muted-foreground py-8">로딩 중...</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>아이디</TableHead>
                        <TableHead>닉네임</TableHead>
                        <TableHead>가입일</TableHead>
                        <TableHead className="text-right">관리</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allMembers.map((member) => (
                        <TableRow key={member.memberId}>
                          <TableCell className="font-mono text-sm">{member.memberId}</TableCell>
                          <TableCell className="font-medium">{member.nickname}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(member.createdAt)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() =>
                                handleDeleteMember(member.memberId, member.nickname, false)
                              }
                            >
                              강제 탈퇴
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* 가입 대기 목록 */}
          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle>가입 승인 대기</CardTitle>
                <CardDescription>
                  승인을 기다리는 {pendingMembers.length}명의 회원이 있습니다.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingMembers.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    승인 대기 중인 회원이 없습니다.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>아이디</TableHead>
                        <TableHead>닉네임</TableHead>
                        <TableHead>신청일</TableHead>
                        <TableHead className="text-right">관리</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingMembers.map((member) => (
                        <TableRow key={member.memberId}>
                          <TableCell className="font-mono text-sm">{member.memberId}</TableCell>
                          <TableCell className="font-medium">{member.nickname}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(member.createdAt)}
                          </TableCell>
                          <TableCell className="text-right space-x-2">
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleApproveMember(member.memberId, member.nickname)}
                            >
                              승인
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleDeleteMember(member.memberId, member.nickname, true)
                              }
                            >
                              거절
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Discord Bot */}
          <TabsContent value="discord">
            <Card>
              <CardHeader>
                <CardTitle>Discord Bot 관리</CardTitle>
                <CardDescription>
                  Discord Bot을 켜거나 끌 수 있습니다. Bot이 켜져있으면 일정 생성 시 Discord에 알림을
                  전송합니다.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <p className="font-medium">Bot 상태</p>
                    <p className="text-sm text-muted-foreground">
                      {discordBotStatus || 'Bot 상태를 확인하려면 토글 버튼을 클릭하세요'}
                    </p>
                  </div>
                  <Button
                    variant={discordBotStatus === '봇on' ? 'destructive' : 'default'}
                    onClick={handleToggleDiscordBot}
                    disabled={botLoading}
                    className="gap-2"
                  >
                    <Bot className="h-4 w-4" />
                    {botLoading ? '처리 중...' : discordBotStatus === '봇on' ? 'Bot 끄기' : 'Bot 켜기'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
